import { FC, lazy } from 'react'
import { RouteObject, useRoutes } from 'react-router-dom'

import { IS_JUME_ENV } from 'constants/config'
import { Routes } from 'constants/routes'
import { AuthLayout } from 'layouts/AuthLayout'
import { MainLayout } from 'layouts/MainLayout'

const LoginPage = lazy(() => import(/* webpackChunkName: "login-chunk" */ 'pages/LoginPage'))
const MainPage = lazy(() => import(/* webpackChunkName: "main-chunk" */ 'pages/MainPage'))
const ProfilePage = lazy(() => import(/* webpackChunkName: "main-chunk" */ 'pages/ProfilePage'))
const ProjectsPage = lazy(() => import(/* webpackChunkName: "projects-chunk" */ 'pages/ProjectsPage'))
const UsersPage = lazy(() => import(/* webpackChunkName: "users-chunk" */ 'pages/UsersPage'))
const GroupsPage = lazy(() => import(/* webpackChunkName: "users-chunk" */ 'pages/GroupsPage'))
const PermissionsPage = lazy(() => import(/* webpackChunkName: "users-chunk" */ 'pages/PermissionsPage'))
const TemplatesPage = lazy(() => import(/* webpackChunkName: "templates-chunk" */ 'pages/TemplatesPage'))
const TemplateFieldsPage = lazy(() => import(/* webpackChunkName: "templates-chunk" */ 'pages/TemplateFieldsPage'))
const BusinessTypesPage = lazy(() => import(/* webpackChunkName: "templates-chunk" */ 'pages/BusinessTypesPage'))
const FieldsPage = lazy(() => import(/* webpackChunkName: "templates-chunk" */ 'pages/FieldsPage'))
const PromoGroupsPage = lazy(() => import(/* webpackChunkName: "promo-chunk" */ 'pages/PromoGroupsPage'))
const PromoPeriodsPage = lazy(() => import(/* webpackChunkName: "promo-chunk" */ 'pages/PromoPeriodsPage'))
const PromoHierarchyLevelsPage = lazy(
  () => import(/* webpackChunkName: "promo-chunk" */ 'pages/PromoHierarchyLevelsPage'),
)
const PromoTargetVariablesPage = lazy(
  () => import(/* webpackChunkName: "promo-chunk" */ 'pages/PromoTargetVariablesPage'),
)
const PromoConstraintsPage = lazy(() => import(/* webpackChunkName: "promo-chunk" */ 'pages/PromoConstraintsPage'))
const PromoLogsPage = lazy(() => import(/* webpackChunkName: "promo-chunk" */ 'pages/PromoLogsPage'))
const RtmGroupsPage = lazy(() => import(/* webpackChunkName: "rtm-chunk" */ 'pages/RtmGroupsPage'))
const RtmParametersPage = lazy(() => import(/* webpackChunkName: "rtm-chunk" */ 'pages/RtmParametersPage'))
const TetrisGroupsPage = lazy(() => import(/* webpackChunkName: "tetris-chunk" */ 'pages/TetrisGroupsPage'))
const TetrisDefaultPricesPage = lazy(
  () => import(/* webpackChunkName: "tetris-chunk" */ 'pages/TetrisDefaultPricesPage'),
)
const TetrisLogsPage = lazy(() => import(/* webpackChunkName: "tetris-chunk" */ 'pages/TetrisLogsPage'))
const CfrGroupsPage = lazy(() => import(/* webpackChunkName: "cfr-chunk" */ 'pages/CfrGroupsPage'))
const CfrDefaultParamsPage = lazy(() => import(/* webpackChunkName: "cfr-chunk" */ 'pages/CfrDefaultParamsPage'))
const ConfigPage = lazy(() => import(/* webpackChunkName: "config-chunk" */ 'pages/ConfigPage'))
const CfrLogsPage = lazy(() => import(/* webpackChunkName: "cfr-chunk" */ 'pages/CfrLogsPage'))
const ManualsPage = lazy(() => import(/* webpackChunkName: "manuals-chunk" */ 'pages/ManualsPage'))
const ValidationPage = lazy(() => import(/* webpackChunkName: "validation-chunk" */ 'pages/ValidationPage'))

const routes: RouteObject[] = [
  {
    path: Routes.Login,
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: <LoginPage />,
      },
    ],
  },
  {
    path: Routes.Main,
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <MainPage />,
      },
      {
        path: Routes.PrgmManuals,
        element: <ManualsPage />,
      },
      {
        path: Routes.PrgmValidation,
        element: <ValidationPage />,
      },
      {
        path: Routes.Profile,
        element: <ProfilePage />,
      },
      {
        path: Routes.Projects,
        element: <ProjectsPage />,
      },
      {
        path: Routes.Users,
        element: <UsersPage />,
      },
      {
        path: Routes.Groups,
        element: <GroupsPage />,
      },
      {
        path: Routes.Permissions,
        element: <PermissionsPage />,
      },
      {
        path: Routes.Templates,
        element: <TemplatesPage />,
      },
      {
        path: Routes.TemplateFields,
        element: <TemplateFieldsPage />,
      },
      {
        path: Routes.BusinessTypes,
        element: <BusinessTypesPage />,
      },
      {
        path: Routes.Fields,
        element: <FieldsPage />,
      },
      {
        path: Routes.PromoPeriods,
        element: <PromoPeriodsPage />,
      },
      {
        path: Routes.PromoGroups,
        element: <PromoGroupsPage />,
      },
      {
        path: Routes.PromoHierarchyLevels,
        element: <PromoHierarchyLevelsPage />,
      },
      {
        path: Routes.PromoTargetVariables,
        element: <PromoTargetVariablesPage />,
      },
      {
        path: Routes.PromoConstraints,
        element: <PromoConstraintsPage />,
      },
      {
        path: Routes.PromoLogs,
        element: <PromoLogsPage />,
      },
      {
        path: Routes.RtmGroups,
        element: <RtmGroupsPage />,
      },
      {
        path: Routes.RtmParameters,
        element: <RtmParametersPage />,
      },
      {
        path: Routes.TetrisGroups,
        element: <TetrisGroupsPage />,
      },
      {
        path: Routes.TetrisDefaultPrices,
        element: <TetrisDefaultPricesPage />,
      },
      {
        path: Routes.TetrisLogs,
        element: <TetrisLogsPage />,
      },
      {
        path: Routes.CfrGroups,
        element: <CfrGroupsPage />,
      },
      {
        path: Routes.CfrPfrParams,
        element: <CfrDefaultParamsPage />,
      },
      {
        path: Routes.CfrLogs,
        element: <CfrLogsPage />,
      },
    ],
  },
]

if (IS_JUME_ENV) {
  routes[1].children?.push({
    path: Routes.Config,
    element: <ConfigPage />,
  })
}

export const RenderRoute: FC = () => useRoutes(routes)
